/**
 * **Could reach you next turn** (`projects/move-advisory.md` 1.2): which hostiles could attack a
 * unit standing at a cell on their next turn, the move advisory's last line.
 *
 * Each hostile's reach is the movement wash's own flood (`reachableForCombatant` over
 * `planningTurnState`: a fresh turn's Speed from where it stands, terrain costs included) with the
 * unit already standing at the cell, grown by the hostile's longest weapon Range or Threat. Line of
 * sight is ignored and a Boost is not added, so the answer is a cheap "could", run per drag cell
 * (decision owed 1, defaulted to the tracker's recommendation). A hostile that may not move
 * (Immobilized, Stunned) reaches only from where it stands.
 */
import {
  isAble,
  isActor,
  planningTurnState,
  primaryReach,
  reachableForCombatant,
  sideOf,
  threatValue,
  worldFor,
  type Cell,
  type Combatant,
  type EncounterState,
} from "@lancer-vtt/domain";

/**
 * The ids (sorted) of able hostile actors that could attack `moverId` standing at `cell` on their
 * next turn. Pure, no dice. `[]` for an unknown mover.
 */
export function couldReachNextTurn(state: EncounterState, cell: Cell, moverId: string): string[] {
  const mover = state.combatants[moverId];
  if (mover === undefined) return [];
  const moved: EncounterState = { ...state, combatants: { ...state.combatants, [moverId]: { ...mover, pos: cell } } };
  const world = worldFor(state);
  const target = world.footprint(cell, mover.base.size).cells();
  const out: string[] = [];
  for (const id of Object.keys(state.combatants).sort()) {
    const hostile = moved.combatants[id]!;
    if (id === moverId || hostile.pos == null || !isActor(hostile) || !isAble(hostile) || sideOf(hostile) === sideOf(mover)) continue;
    const reach = longestReach(hostile);
    const anchors = reachableForCombatant(planningTurnState(moved, id), hostile);
    const from = anchors === null ? [hostile.pos] : [...anchors.values()].map((r) => r.cell);
    const inReach = from.some((anchor) =>
      world.footprint(anchor, hostile.base.size).cells().some((c) => target.some((t) => world.distance(c, t) <= reach)),
    );
    if (inReach) out.push(id);
  }
  return out;
}

/** The farthest a combatant can attack: its longest weapon Range or Threat, and never under 1 (Ram, Grapple and the improvised attack all reach an adjacent target). */
export function longestReach(c: Combatant): number {
  let reach = 1;
  for (const w of c.base.weapons ?? []) {
    const range = w.range ?? [];
    reach = Math.max(reach, primaryReach(range), threatValue(range));
  }
  return reach;
}
